import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'decrement', 'increment', 'error', 'errorText'];
  static values = {
    delay: { type: Number, default: 500 },
    min: { type: Number, default: 0 },
    max: Number,
    errorMessage: String,
  };

  disconnect() {
    clearTimeout(this.timer);
  }

  decrement() {
    this.apply(this.quantity - 1);
  }

  increment() {
    this.apply(this.quantity + 1);
  }

  typed(event) {
    if (event.detail?.debounced) return;

    event.stopPropagation();
    const quantity = this.quantity;

    if (this.hasMaxValue && quantity > this.maxValue) {
      this.markInvalid();
      clearTimeout(this.timer);
      return;
    }

    // Live typing: clear the max error, but only commit/autosave on change (blur/Enter).
    if (event.type === 'input') {
      this.clearInvalid();
      this.syncButtons(Math.max(quantity, this.minValue));
      return;
    }

    this.apply(quantity);
  }

  apply(quantity) {
    let next = Math.max(quantity, this.minValue);
    if (this.hasMaxValue) next = Math.min(next, this.maxValue);
    this.clearInvalid();
    this.inputTarget.value = next;
    this.syncButtons(next);
    this.element.closest('.upsell-product')?.classList.toggle('upsell-product--selected', next > 0);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.inputTarget.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { debounced: true } }));
    }, this.delayValue);
  }

  markInvalid() {
    const message = this.hasErrorMessageValue ? this.errorMessageValue : '';
    this.inputTarget.setCustomValidity(message || 'Invalid');
    this.syncButtons(this.quantity);
    if (this.hasErrorTextTarget && message) this.errorTextTarget.textContent = message;
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
  }

  clearInvalid() {
    this.inputTarget.setCustomValidity('');
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
  }

  syncButtons(quantity) {
    this.decrementTarget.disabled = quantity <= this.minValue;
    if (this.hasIncrementTarget) {
      this.incrementTarget.disabled = this.hasMaxValue && quantity >= this.maxValue;
    }
  }

  get quantity() {
    return Number.parseInt(this.inputTarget.value, 10) || 0;
  }
}
